import dayjs from 'dayjs';
import { extractWarnings } from '../../../WeeklyGLPosting/utils/glPosting';
import { DATE_FORMAT_API } from '../utils/monthlyReconciliationProductHelpers';
import {
  buildMonthlyValidationIssueRows,
  formatValidationErrors,
  getMonthlyFeedsErrorMessage,
  getMonthlyFeedsErrorTitle,
  isNoWeeklyPostingsError,
} from '../utils/monthlyReconciliationValidationHelpers';
const UI_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export const EMPTY_MONTHLY_ERROR_MODAL = {
  open: false,
  title: '',
  message: '',
  errors: [],
  issueRows: [],
  showIssueTable: false,
};

const EMPTY_FINALIZED_WINDOW = {
  exists: false,
  posting: null,
  hasNewTransactionUpdates: false,
  readOnly: false,
  messageType: null,
  message: '',
};

const normalizeResyncData = (resync) => {
  if (!resync) return [];

  const values = Array.isArray(resync) ? resync : Object.values(resync);

  return values.reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc.push(...normalizeResyncData(item));
    } else if (item) {
      acc.push(item);
    }

    return acc;
  }, []);
};

const normalizeMessage = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getProductCategoryById = (productCategories, productId) =>
  Array.isArray(productCategories)
    ? productCategories.find((product) => Number(product?.id) === Number(productId))
    : null;

const formatWeekRange = (start, end) => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (!startDate.isValid() || !endDate.isValid()) return '—';

  return `${startDate.format('MM/DD')} - ${endDate.format('MM/DD')}`;
};

const formatPostingDateValue = (value) => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '—';
};

export const formatMonthlyUiDate = (value) => {
  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format(DATE_FORMAT_API) : '';
};

export const toMonthlyApiDate = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const match = value.trim().match(UI_DATE_PATTERN);

    if (match) {
      const [, month, day, year] = match;
      return `${year}-${month}-${day}`;
    }
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

export const buildAppliedMonthlyFilters = ({
  selectedProductType,
  productCategories,
  productNamesById,
  startDate,
  endDate,
}) => {
  if (!selectedProductType) return null;

  const category = getProductCategoryById(productCategories, selectedProductType);
  const formattedStartDate = formatMonthlyUiDate(startDate);
  const formattedEndDate = formatMonthlyUiDate(endDate);

  if (!formattedStartDate || !formattedEndDate) return null;

  return {
    productId: Number(selectedProductType),
    productName: productNamesById?.[String(selectedProductType)] ?? category?.x3_productname ?? category?.name ?? '',
    x3_productname: category?.x3_productname ?? '',
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  };
};

export const buildMonthlyRequestPayload = ({ appliedFilters, productCategories }) => {
  if (!appliedFilters?.productId) return null;

  const category = getProductCategoryById(productCategories, appliedFilters.productId);
  const startDate = toMonthlyApiDate(appliedFilters.startDate);
  const endDate = toMonthlyApiDate(appliedFilters.endDate);

  if (!startDate || !endDate) return null;

  return {
    productId: Number(appliedFilters.productId),
    x3ProductName: category?.x3_productname ?? appliedFilters.x3_productname ?? appliedFilters.productName ?? '',
    startDate,
    endDate,
  };
};

export const mapMonthlyFeedsResponseToSummary = (data, x3ProductName) => {
  const postings = Array.isArray(data?.postings) ? data.postings : [];
  const resyncData = normalizeResyncData(data?.resync ?? data?.data?.resync);
  const mappedPostings = postings.map((posting) => ({
    weekRange: formatWeekRange(posting.startDate, posting.endDate),
    postingDate: formatPostingDateValue(posting.postingDate),
    postingId: posting.postingId ?? '—',
    creditCardPosted: posting.creditPosted ?? 0,
    cashPosted: posting.cashPosted ?? 0,
    creditCardCurrent: posting.creditCurrent ?? posting.creditPosted ?? 0,
    cashCurrent: posting.cashCurrent ?? posting.cashPosted ?? 0,
    refundCurrent: posting.refundCurrent ?? 0,
    creditVariance: posting.creditVariance ?? 0,
    cashVariance: posting.cashVariance ?? 0,
    refundVariance: posting.refundVariance ?? 0,
    sublocations: posting.sublocations ?? null,
  }));

  const sublocationNameById = {};
  postings.forEach((posting) => {
    const sublocations = posting?.sublocations?.selected ?? posting?.sublocations ?? [];
    (Array.isArray(sublocations) ? sublocations : []).forEach((sublocation) => {
      if (sublocation?.id != null) sublocationNameById[String(sublocation.id)] = sublocation?.name ?? '';
    });
  });

  const extractedWarnings = extractWarnings({ data }, { sublocationNameById });
  const sublocationWarnings = extractedWarnings.map((warning, index) => ({
    id: `w-${index}`,
    message: warning?.message ?? '—',
    assetId: warning?.assetId != null ? String(warning.assetId) : null,
    assetLabel: warning?.assetLabel ?? '',
  }));

  const finalizedWindow =
    data?.finalizedWindow && typeof data.finalizedWindow === 'object'
      ? {
          ...data.finalizedWindow,
          posting: data.finalizedWindow?.posting ?? null,
        }
      : EMPTY_FINALIZED_WINDOW;

  const assetWithVarianceArray = Array.isArray(data?.assetWithVariance) ? data.assetWithVariance : [];

  return {
    postings: mappedPostings,
    creditCardCurrent: 0,
    cashCurrent: 0,
    totalAssets: Number(data?.totalAssetsCount ?? 0),
    assetsWithVariance: assetWithVarianceArray.length,
    assetWithVarianceData: assetWithVarianceArray,
    varianceTable: [],
    missingDataFeedCount: Number(data?.missingDataFeedCount ?? 0),
    totalUnmappedDataFeed: Number(data?.totalUnmappedDataFeed ?? 0),
    sublocationWarnings,
    x3ProductName: x3ProductName ?? '',
    finalizedWindow,
    resyncData,
  };
};

export const mapMonthlySummaryToReconciliationData = ({ appliedFilters, reconciliationSummary }) => {
  if (!appliedFilters || !reconciliationSummary) return null;

  return {
    totalAssets: reconciliationSummary.totalAssets ?? 0,
    assetsWithVariance: Number(reconciliationSummary.assetsWithVariance ?? 0),
    missingDataFeedCount: reconciliationSummary.missingDataFeedCount ?? 0,
    totalUnmappedDataFeed: reconciliationSummary.totalUnmappedDataFeed ?? 0,
    sublocationWarnings: reconciliationSummary.sublocationWarnings ?? [],
    assetWithVarianceData: reconciliationSummary.assetWithVarianceData ?? [],
  };
};

export const mapMonthlySummaryToWeeklyPostingsData = ({ appliedFilters, reconciliationSummary }) => {
  if (!appliedFilters || !reconciliationSummary?.postings?.length) return [];

  return reconciliationSummary.postings.map((posting) => {
    const creditCardPosted = posting.creditCardPosted ?? 0;
    const creditCardCurrent = posting.creditCardCurrent ?? posting.creditCardPosted ?? 0;
    const cashPosted = posting.cashPosted ?? 0;
    const cashCurrent = posting.cashCurrent ?? posting.cashPosted ?? 0;
    const refundCurrent = posting.refundCurrent ?? 0;
    const creditVariance = posting.creditVariance ?? 0;
    const cashVariance = posting.cashVariance ?? 0;
    const refundVariance = posting.refundVariance ?? 0;

    return {
      weekLabel: posting.weekRange,
      postingDate: posting.postingDate,
      postingId: posting.postingId ?? '—',
      creditCardPosted,
      creditCardCurrent,
      creditVariance,
      cashPosted,
      cashCurrent,
      refundCurrent,
      cashVariance,
      productType: reconciliationSummary.x3ProductName || appliedFilters.productName,
      totalCurrent: creditCardCurrent + cashCurrent - refundCurrent,
      totalVariance: creditVariance + cashVariance - refundVariance,
      sublocations: posting.sublocations ?? null,
    };
  });
};

export const getFinalizedWindowAlertState = ({ appliedFilters, reconciliationSummary }) => {
  const finalizedWindow = reconciliationSummary?.finalizedWindow;

  if (!appliedFilters || !finalizedWindow?.exists || !finalizedWindow?.message) return null;

  return {
    severity: finalizedWindow.messageType === 'warning' ? 'warning' : 'info',
    message: finalizedWindow.message,
  };
};

export const getMonthlyValidationModalState = (error) => {
  const errorData = error?.data || error;
  const validationErrors = errorData?.validationError ?? errorData?.validationErrors ?? [];

  if (!(validationErrors.length > 0 || error?.status === 422)) return null;

  const shouldSuppressValidationDetails = isNoWeeklyPostingsError(errorData);
  const mainMessage = getMonthlyFeedsErrorMessage(errorData);
  const issueRows = buildMonthlyValidationIssueRows(validationErrors);
  const showIssueTable = !shouldSuppressValidationDetails && issueRows.length > 0;
  const normalizedMainMessage = normalizeMessage(mainMessage);
  const dedupedErrors = formatValidationErrors(validationErrors).filter(
    (validationMessage) => normalizeMessage(validationMessage) !== normalizedMainMessage,
  );

  return {
    open: true,
    title: getMonthlyFeedsErrorTitle(errorData),
    message: mainMessage,
    errors: shouldSuppressValidationDetails || showIssueTable ? [] : dedupedErrors,
    issueRows: showIssueTable ? issueRows : [],
    showIssueTable,
  };
};
